import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, exhaustMap, tap } from 'rxjs/operators';
import * as _ from 'underscore';
import { Api } from 'src/app/shared/constants/api';
import { APP_ROUTES } from 'src/app/shared/constants/approutes';
import { Message } from 'src/app/shared/constants/message';
import { PermissionKey } from 'src/app/shared/constants/permissionCode';
import { IPermissionWithShow } from 'src/app/shared/interface';
import { DataService } from 'src/app/shared/services/data.service';
import { PermissionService } from 'src/app/shared/services/permissions.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { ICustomerServiceRequirement, ICustomerServiceRequirementCriteria } from '../customer-service-requirement.interface';

@Component({
    selector: 'app-customer-service-requirement-list',
    templateUrl: './customer-service-requirement-list.component.html',
    styleUrls: ['./customer-service-requirement-list.component.less'],
    standalone: false
})
export class CustomerServiceRequirementListComponent implements OnInit, AfterViewInit, OnDestroy {

  totalRecords: any;
  columns = ['businessSegmentName', 'customerName', 'customerBranchName', 'action'];
  pageSizeOptions: number[] = this.utilityService.getPageSizeOptions();
  dataSource = new MatTableDataSource<ICustomerServiceRequirement>();
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  listForm: FormGroup;
  criteria: ICustomerServiceRequirementCriteria = {};
  addPermission: boolean = false;
  editPermission: boolean = false;
  viewPermission: boolean = false;
  subscription = new Subscription();
  private searchTrigger$ = new Subject<boolean>();

  constructor(private logger: NGXLogger, private service: DataService,
    private router: Router, private activatedRoute: ActivatedRoute,
    private utilityService: UtilityService, private formBuilder: FormBuilder,
    private permissionService: PermissionService) { }

  ngOnInit(): void {
    this.getAllPermissions();
    this.initializeForm();
    this.setupSearchPipeline();
    this.setBusinessSegmentNameValueChangeEvent();
    this.setCustomerNameValueChangeEvent();
    this.setCustomerBranchNameValueChangeEvent();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.paginator?.page?.pipe(tap(() => this.getList())).subscribe();
    this.getList();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.searchTrigger$.complete();
  }

  private setBusinessSegmentNameValueChangeEvent() {
    const sub = this.businessSegmentName.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(value => {
      this.criteria.businessSegmentName = value;
      this.getList(true);
    });
    this.subscription.add(sub);
  }

  private setCustomerNameValueChangeEvent() {
    const sub = this.customerName.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(value => {
      this.criteria.customerName = value;
      this.getList(true);
    });
    this.subscription.add(sub);
  }

  private setCustomerBranchNameValueChangeEvent() {
    const sub = this.customerBranchName.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(value => {
      this.criteria.customerBranchName = value;
      this.getList(true);
    });
    this.subscription.add(sub);
  }

  initializeForm() {
    this.listForm = this.formBuilder.group({
      businessSegmentName: [],
      customerName: [],
      customerBranchName: []
    });
  }

  sortData(event: any) {
    this.criteria.sort = [event.active, event.direction];
    this.getList(true);
  }

  openForm(id?: number) {
    if (id) {
      this.editPermission
        ? this.router.navigate(['edit/' + id], { relativeTo: this.activatedRoute })
        : this.utilityService.notifier(Message.EDIT_PERMISSION_WARNING, 'warning');
    } else {
      this.addPermission
        ? this.router.navigate(['add'], { relativeTo: this.activatedRoute })
        : this.utilityService.notifier(Message.EDIT_PERMISSION_WARNING, 'warning');
    }
  }



  private setupSearchPipeline() {
    this.searchTrigger$.pipe(
      exhaustMap(resetPageIndex => {
        if (resetPageIndex) this.paginator.pageIndex = 0;
        this.criteria.page = this.paginator?.pageIndex;
        this.criteria.size = this.paginator?.pageSize;
        this.criteria.sort = this.criteria.sort ?? ['createdDate', 'desc'];
        if (!this.viewPermission) {
          this.utilityService.notifier(Message.VIEW_PERMISSION_WARNING, 'warning');
          return of(null);
        }
        return this.service.get(Api.CUSTOMER_SERVICE_REQUIREMENT, this.criteria);
      })
    ).subscribe(res => {
      if (!res) return;
      if (res.contents.length === 0) {
        this.utilityService.notifier('No records found', 'info');
      }
      this.logger.info('Customer Service Requirement List', res);
      this.dataSource.data = res.contents;
      this.totalRecords = res.totalRecords;
      if (this.paginator.pageIndex >= Math.ceil(this.totalRecords / this.paginator.pageSize)) {
        this.paginator.pageIndex = 0;
      }
    });
  }

  getList(resetPageIndex: boolean = false) {
    this.searchTrigger$.next(resetPageIndex);
  }


  public getAllPermissions() {
    const permissionCodes = [
      PermissionKey.CUSTOMER_SERVICE_REQUIREMENT_MODULE_ADD_PERMISSION,
      PermissionKey.CUSTOMER_SERVICE_REQUIREMENT_MODULE_EDIT_PERMISSION,
      PermissionKey.CUSTOMER_SERVICE_REQUIREMENT_MODULE_VIEW_PERMISSION
    ];
    const permissionsWithShowValue: IPermissionWithShow[] =
      this.permissionService.checkPermissions(permissionCodes, PermissionKey.CUSTOMER_SERVICE_REQUIREMENT_MODULE);
    this.addPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.CUSTOMER_SERVICE_REQUIREMENT_MODULE_ADD_PERMISSION })?.show ? true : false;
    this.editPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.CUSTOMER_SERVICE_REQUIREMENT_MODULE_EDIT_PERMISSION })?.show ? true : false;
    this.viewPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.CUSTOMER_SERVICE_REQUIREMENT_MODULE_VIEW_PERMISSION })?.show ? true : false;
  }

  get businessSegmentName() { return this.listForm.get('businessSegmentName'); }
  get customerName() { return this.listForm.get('customerName'); }
  get customerBranchName() { return this.listForm.get('customerBranchName'); }
}
